/**
 * Service for tracking recently used blocks and favorite blocks.
 * Stores data in localStorage for persistence.
 */

const RECENT_STORAGE_KEY = 'scubamail_recent_blocks';
const FAVORITES_STORAGE_KEY = 'scubamail_favorite_blocks';
const MAX_RECENT_ITEMS = 10;

export interface RecentBlockItem {
    type: string;
    timestamp: number;
}

export const recentBlocksService = {
    /**
     * Track usage of a block type
     */
    trackUsage(blockType: string): void {
        try {
            const recent = this.getRecent();
            // Remove existing entry for this type
            const filtered = recent.filter(item => item.type !== blockType);
            // Add to front
            filtered.unshift({ type: blockType, timestamp: Date.now() });
            // Limit size
            const limited = filtered.slice(0, MAX_RECENT_ITEMS);
            localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(limited));
        } catch (error) {
            console.error('Error tracking block usage:', error);
        }
    },

    /**
     * Get recently used block types
     */
    getRecent(limit: number = MAX_RECENT_ITEMS): RecentBlockItem[] {
        try {
            const stored = localStorage.getItem(RECENT_STORAGE_KEY);
            if (!stored) return [];
            const parsed = JSON.parse(stored) as RecentBlockItem[];
            return parsed.slice(0, limit);
        } catch (error) {
            console.error('Error loading recent blocks:', error);
            return [];
        }
    },

    /**
     * Get recent block types as a simple array
     */
    getRecentTypes(limit: number = MAX_RECENT_ITEMS): string[] {
        return this.getRecent(limit).map(item => item.type);
    },

    /**
     * Toggle favorite status for a block type
     */
    toggleFavorite(blockType: string): boolean {
        try {
            const favorites = this.getFavorites();
            const isFavorite = favorites.includes(blockType);

            if (isFavorite) {
                const filtered = favorites.filter(type => type !== blockType);
                localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(filtered));
                return false;
            } else {
                favorites.push(blockType);
                localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
                return true;
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
            return false;
        }
    },

    /**
     * Check if a block type is favorited
     */
    isFavorite(blockType: string): boolean {
        return this.getFavorites().includes(blockType);
    },

    /**
     * Get all favorited block types
     */
    getFavorites(): string[] {
        try {
            const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
            if (!stored) return [];
            return JSON.parse(stored) as string[];
        } catch (error) {
            console.error('Error loading favorites:', error);
            return [];
        }
    },

    /**
     * Clear all recent blocks
     */
    clearRecent(): void {
        localStorage.removeItem(RECENT_STORAGE_KEY);
    },

    /**
     * Clear all favorites
     */
    clearFavorites(): void {
        localStorage.removeItem(FAVORITES_STORAGE_KEY);
    },
};
